/**
 * The shorts framing transform.
 *
 * Pure geometry, deliberately in its own module: it is the one piece of this
 * feature that must be verifiable in isolation, and importing the editor
 * component to test it drags in the whole API client.
 */

export const clamp = (v: number, lo: number, hi: number) =>
  Math.min(Math.max(v, lo), hi);

/**
 * Source camera pixels. The transform depends on the real pixel ratio
 * outW/srcW, not just aspect, so these are dimensions and not a 16:9 constant.
 * ReaperMobile §21.1: "With 1920×1080 cameras into 1080×1920".
 */
const SRC_W = 1920;
const SRC_H = 1080;
/** Output frame: a vertical Short. */
const OUT_W = 1080;
const OUT_H = 1920;

/**
 * The crop rectangle, as fractions of the source frame.
 *
 * A direct port of `M.cropFor` in ReaperMobile's `lib/short_render.lua`
 * (portal doc §21.1) — the same function both of their render engines use, so
 * this box is what actually gets rendered rather than our reading of it.
 *
 * The thing that is easy to get wrong, and that we did get wrong: posX/posY
 * position the IMAGE in the output frame, the way Premiere's Motion "center"
 * does. posX > 0.5 slides the picture right, which moves the crop LEFT. They
 * are neither the crop centre nor a fraction of the pannable range.
 *
 * cx/cy are computed from the UNCLAMPED visible size, then the size is capped
 * at 1 and the origin clamped into the frame — in that order, as in the Lua.
 */
export function cropRect(posX: number, posY: number, scale: number) {
  const s = (Number.isFinite(scale) ? scale : 100) / 100;
  const rawW = (OUT_W / SRC_W) / s;
  const rawH = (OUT_H / SRC_H) / s;
  const px = Number.isFinite(posX) ? posX : 0.5;
  const py = Number.isFinite(posY) ? posY : 0.5;
  const cx = 0.5 - (px - 0.5) * rawW;
  const cy = 0.5 - (py - 0.5) * rawH;
  const width = Math.min(rawW, 1);
  const height = Math.min(rawH, 1);
  return {
    width,
    height,
    rawW,
    rawH,
    left: clamp(cx - width / 2, 0, 1 - width),
    top: clamp(cy - height / 2, 0, 1 - height),
  };
}

/** REAPER ignores out-of-range values rather than clamping them (§21.2). */
export const SCALE_MIN = 50;
export const SCALE_MAX = 500;

// ── REAPER parity (portal doc §24.2) ────────────────────────────────────────
//
// The portal mirrors REAPER's framing UI deliberately: the same clip framed in
// two tools that disagree about what a control MEANS is worse than having only
// one of them, and whoever uses both will trust whichever they learned first.

/**
 * Cover-fill minimum zoom: `max(outW/srcW, outH/srcH) * 100`.
 *
 * 177.78 for a 16:9 camera into 9:16. REAPER clamps to this after every
 * change so the frame is always filled and never shows black — its slider can
 * go lower but snaps back. Ours must refuse the same values, or the portal can
 * set a framing REAPER cannot reproduce.
 */
export const COVER_MIN_SCALE =
  Math.max(OUT_W / SRC_W, OUT_H / SRC_H) * 100;

/** REAPER accepts 50–500, but clamps up to cover; this is the usable range. */
export const SCALE_SLIDER_MAX = SCALE_MAX;

/** Clamp a zoom the way REAPER does. */
export function clampScale(scale: number): number {
  const s = Number.isFinite(scale) ? scale : COVER_MIN_SCALE;
  return clamp(s, COVER_MIN_SCALE, SCALE_MAX);
}

/**
 * Pan limits for a given zoom.
 *
 * REAPER keeps posX/posY within 0–1 AND keeps the crop inside the source. At
 * cover zoom that means posX 0–1 only moves the crop centre between 0.342 and
 * 0.658 of the source width: a person at the far edge of a wide camera cannot
 * be centred. ReaperMobile flagged that as an existing limit rather than a
 * design choice — we reproduce it so a value set here always renders.
 */
export function clampPos(posX: number, posY: number) {
  return {
    posX: clamp(Number.isFinite(posX) ? posX : 0.5, 0, 1),
    posY: clamp(Number.isFinite(posY) ? posY : 0.5, 0, 1),
  };
}

/**
 * Drag handling, REAPER's metaphor: the user grabs the PICTURE inside a
 * phone-shaped window, not a crop box over the source. Dragging right moves
 * the person right, which is `posX += dx / previewWidth`.
 *
 * Our first version dragged a crop box over the uncropped still, which is the
 * opposite feel for the same numbers.
 */
export function dragToPos(
  startX: number, startY: number,
  dxFraction: number, dyFraction: number,
) {
  return clampPos(startX + dxFraction, startY + dyFraction);
}

/** The three levels REAPER distinguishes, with its exact wording. */
export type FramingLevel = 'camera' | 'short' | 'shot';

export const FRAMING_LABEL: Record<FramingLevel, string> = {
  camera: 'using the camera framing',
  short: 'framed for this short',
  shot: 'framed for this shot',
};

/**
 * Which level a camera's shots are at.
 *
 * REAPER has three; `render_params` only has two states per shot, because a
 * SHORT-level override is written as `custom: true` on every one of that
 * camera's shots. So "all custom and all identical" means short-level, and a
 * mix means per-shot.
 */
export function framingLevel(
  shots: Array<{ camera: string; posX: number; posY: number; scale: number; custom?: boolean }>,
  camera: string,
): FramingLevel {
  const mine = shots.filter((s) => s.camera === camera);
  if (!mine.length || mine.every((s) => s.custom !== true)) return 'camera';
  if (!mine.every((s) => s.custom === true)) return 'shot';
  const [first] = mine;
  const same = mine.every((s) =>
    s.posX === first.posX && s.posY === first.posY && s.scale === first.scale);
  return same ? 'short' : 'shot';
}
